/**
 * THE FIELD MAP — where each person currently is, and HOW RECENT that is.
 * v22-D (owner: "I want to see the map and point out where the sales and
 * support person is in the field").
 *
 * NO MAP LIBRARY, NO BASEMAP, and that is a REPORTED DECISION, not an
 * omission — the reasoning (third-party tiles are a position-bearing channel;
 * self-hosted tiles are owner infrastructure; the team-scale question needs a
 * projected plane with an honest scale bar) lives in `field-map-model.ts`,
 * beside the projection it justifies. Street context is the roster's
 * per-person OpenStreetMap link: ONE coordinate, disclosed by ONE deliberate
 * click of a seat that already holds the raw key — the same act as reading
 * the number off a register into a browser.
 *
 * ⚠ FRESHNESS IS THE SCREEN'S CENTRAL CLAIM. Every marker wears its age ON
 * THE CANVAS (not in a tooltip a reader must know to seek): a live marker
 * says "live", a stamp says how old it is, and stale markers dress hollow.
 * Ages are computed against `server_time` from the payload — never the
 * browser clock, which belongs to whoever configured the laptop.
 *
 * The refusal follows the attendance register's D5 shape: a named refusal,
 * not an empty map (which would read as "nobody is in the field"). The
 * tracking-disabled state is its own sentence for the same reason.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Card, Chip, EmptyState, SkeletonRows } from '../../components/ui';
import { avatarHueIndex } from '../../components/ui/avatar-hue';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { fetchFieldMap } from './api';
import type { FieldMapPerson } from './api';
import {
  SOURCE_LABEL,
  ageSentence,
  fitView,
  freshnessOf,
} from './field-map-model';
import { errorSentence } from '../admin/states';
import { FieldTileMap, TILE_URL_TEMPLATE } from './FieldTileMap';

/**
 * Marker fills per avatar-hue slot. The avatar system's own eight hues, by
 * index — the same identity→hue mapping as every chip and disc, so a person
 * is the same colour on the map as in the register beside it.
 */
const HUE_FILL: readonly string[] = [
  'var(--crf-avatar-1-bg)',
  'var(--crf-avatar-2-bg)',
  'var(--crf-avatar-3-bg)',
  'var(--crf-avatar-4-bg)',
  'var(--crf-avatar-5-bg)',
  'var(--crf-avatar-6-bg)',
  'var(--crf-avatar-7-bg)',
  'var(--crf-avatar-8-bg)',
];

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

export function FieldMapPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can, isGroupAdmin } = useSession();
  const mayView = isGroupAdmin || can(countryCode, 'field.location.view');

  const [selected, setSelected] = useState<string | null>(null);

  /*
   * v23-A — DO WE HAVE A SELF-HOSTED BASEMAP? A one-shot probe of a single
   * tile (world tile z0, same-origin path). Tiles present → the Leaflet
   * basemap; tiles absent (fresh install, not generated yet, or VITE_TILE_URL
   * empty) → the v22 SVG map. Both paths are real; the probe just picks. A
   * `null` means "still checking" and we show the SVG until we know, so the
   * screen never flashes an empty Leaflet frame.
   */
  const [tilesReady, setTilesReady] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    const probe = TILE_URL_TEMPLATE.replace('{z}', '0').replace('{x}', '0').replace('{y}', '0');
    const img = new Image();
    img.onload = () => {
      if (alive) setTilesReady(true);
    };
    img.onerror = () => {
      if (alive) setTilesReady(false);
    };
    img.src = probe;
    return () => {
      alive = false;
    };
  }, []);

  const map = useQuery({
    queryKey: ['field', 'map', countryCode],
    queryFn: ({ signal }) => fetchFieldMap(countryCode, signal),
    enabled: mayView,
    // The near-live cadence the dashboards already set: fresh enough to be
    // useful, far from a tracking stream. Live sessions have their own trail
    // surface; the map is the overview.
    refetchInterval: 60_000,
  });

  const view = useMemo(() => {
    const people = map.data?.people ?? [];
    return fitView(people.map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) })));
  }, [map.data]);

  if (!mayView) {
    // D5 — a designed refusal naming who to ask; an empty map would read as
    // "nobody is in the field", which is a different (and false) sentence.
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="The field map is not available to this account">
          Positions on a map are raw coordinates, and raw coordinates of others need the
          location permission (`field.location.view`) in {countryCode}. Ask a group
          administrator if your role should carry it.
        </Alert>
      </div>
    );
  }

  const payload = map.data;
  const people = payload?.people ?? [];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Field map"
        subtitle="Where each person last was — and how recent that is. A stamp is history, not a live position; only an active live session reads as live."
      />

      <div className="flex flex-col gap-4 px-6 pb-6">
        {map.isPending ? (
          <Card>
            <SkeletonRows rows={4} label="Reading the latest positions…" />
          </Card>
        ) : map.isError ? (
          <Alert tone="danger" title="The map could not be read">
            {errorSentence(map.error, 'The positions could not be read.')} This is a read
            failure — it does not mean nobody is in the field.
          </Alert>
        ) : payload !== undefined && !payload.tracking_enabled ? (
          <Card>
            <EmptyState
              title="Field tracking is switched off in this country"
              description="Every capture source (attendance, visits, live sessions) is disabled in Field settings, so there are no positions to show. This is configuration, not an outage."
            />
          </Card>
        ) : people.length === 0 ? (
          <Card>
            <EmptyState
              title="Nobody has a position on record"
              description="Positions appear when someone clocks in or out, arrives at or leaves a visit, or shares a live session — and they leave the map when the retention purge removes them."
            />
          </Card>
        ) : (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(260px,1fr)]">
            {/* ── the canvas ─────────────────────────────────────────────── */}
            <Card>
              {/*
               * v23-A — SELF-HOSTED TILE BASEMAP when tiles are being served,
               * the v22 SVG projection as the FALLBACK otherwise. `tilesReady`
               * is the one-tile probe; `null` (still checking) shows the SVG so
               * the frame never flashes empty. Markers, freshness and the
               * masked/scoped payload are identical on both — the tiles only
               * change what the pins sit on.
               */}
              {tilesReady === true ? (
                <FieldTileMap
                  people={people}
                  serverTime={payload?.server_time ?? ''}
                  countryCode={countryCode}
                  onSelect={setSelected}
                />
              ) : view === null ? null : (
                <figure aria-label={`Field map of ${countryCode}`}>
                  <svg
                    viewBox={view.viewBox}
                    className="w-full rounded-sm bg-subtle"
                    role="img"
                    aria-label={`${String(people.length)} people plotted`}
                  >
                    {/* the graticule — four quiet lines so the plane reads as a
                      * surface, not a void; spacing carries no meaning beyond that */}
                    {[0.25, 0.5, 0.75].map((f) => {
                      const [x0, y0, w, h] = view.viewBox.split(' ').map(Number) as [
                        number,
                        number,
                        number,
                        number,
                      ];
                      return (
                        <g key={f} stroke="var(--crf-border-default)" strokeWidth={h / 400}>
                          <line x1={x0 + w * f} y1={y0} x2={x0 + w * f} y2={y0 + h} />
                          <line x1={x0} y1={y0 + h * f} x2={x0 + w} y2={y0 + h * f} />
                        </g>
                      );
                    })}

                    {people.map((person) => (
                      <MapMarker
                        key={person.user_id}
                        person={person}
                        serverTime={payload?.server_time ?? ''}
                        view={view}
                        selected={selected === person.user_id}
                        onSelect={() => {
                          setSelected(person.user_id);
                        }}
                      />
                    ))}

                    {/* the scale bar — the projection's honesty, drawn */}
                    {(() => {
                      const [x0, y0, w, h] = view.viewBox.split(' ').map(Number) as [
                        number,
                        number,
                        number,
                        number,
                      ];
                      const barY = y0 + h - h * 0.06;
                      const barX = x0 + w * 0.05;
                      return (
                        <g>
                          <line
                            x1={barX}
                            y1={barY}
                            x2={barX + view.scaleBarUnits}
                            y2={barY}
                            stroke="var(--crf-text-secondary)"
                            strokeWidth={h / 300}
                          />
                          <text
                            x={barX}
                            y={barY - h * 0.015}
                            fontSize={h / 28}
                            fill="var(--crf-text-secondary)"
                          >
                            {view.scaleBarKm >= 1
                              ? `${String(view.scaleBarKm)} km`
                              : `${String(view.scaleBarKm * 1000)} m`}
                          </text>
                        </g>
                      );
                    })()}
                  </svg>
                  <figcaption className="mt-2 flex flex-wrap items-center gap-3 text-label text-fg-secondary">
                    <span className="inline-flex items-center gap-1">
                      <span className="inline-block size-2 rounded-full bg-ok-fg" /> live
                      session
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="inline-block size-2 rounded-full bg-fg-secondary" /> stamp
                      (≤ 30 min)
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="inline-block size-2 rounded-full border border-fg-secondary" />{' '}
                      last known (older)
                    </span>
                    {payload === undefined ? null : (
                      <span className="ml-auto tnum">
                        as of {new Date(payload.server_time).toLocaleTimeString()}
                      </span>
                    )}
                  </figcaption>
                </figure>
              )}
            </Card>

            {/* ── the roster ─────────────────────────────────────────────── */}
            <Card title={`${String(people.length)} in ${countryCode}`}>
              <ul className="divide-y divide-line">
                {people.map((person) => {
                  const fresh = freshnessOf(
                    person.live,
                    person.captured_at,
                    payload?.server_time ?? '',
                  );
                  return (
                    <li
                      key={person.user_id}
                      className={`crf-register-row flex flex-col gap-0.5 py-2.5 ${
                        selected === person.user_id ? 'bg-subtle' : ''
                      }`}
                      onMouseEnter={() => {
                        setSelected(person.user_id);
                      }}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-body font-medium text-fg">{person.name}</span>
                        {/* No colour override on the live chip: status/success/text
                          * measures 3.23 on the chip's own surface in dark — the WORD
                          * is the claim, and the chip's default ink passes both modes. */}
                        {person.live ? (
                          <Chip>
                            live
                            {person.live_ends_at === null
                              ? ''
                              : ` · until ${new Date(person.live_ends_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                          </Chip>
                        ) : null}
                      </div>
                      <span className="text-label text-fg-secondary">
                        {SOURCE_LABEL[person.source] ?? person.source} ·{' '}
                        {fresh === 'live'
                          ? 'streaming now'
                          : `${ageSentence(person.captured_at, payload?.server_time ?? '')}`}
                        {person.accuracy_m === null ? '' : ` · ±${person.accuracy_m} m`}
                      </span>
                      <span className="text-label">
                        {/*
                          * ONE coordinate, ONE deliberate click, by a seat that
                          * already holds raw view — recorded as a decision in the
                          * morning report. noreferrer: the OSM tab learns the
                          * coordinate the holder chose to open, and nothing else.
                          */}
                        <a
                          className="text-indigo-ink underline-offset-2 hover:underline"
                          href={`https://www.openstreetmap.org/?mlat=${person.lat}&mlon=${person.lng}#map=17/${person.lat}/${person.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open in OpenStreetMap
                        </a>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── one marker ── */

function MapMarker({
  person,
  serverTime,
  view,
  selected,
  onSelect,
}: {
  person: FieldMapPerson;
  serverTime: string;
  view: NonNullable<ReturnType<typeof fitView>>;
  selected: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  const { x, y } = view.project(Number(person.lat), Number(person.lng));
  const fresh = freshnessOf(person.live, person.captured_at, serverTime);
  const r = view.height / 40;
  const hue = HUE_FILL[avatarHueIndex(person.user_id)] ?? HUE_FILL[0] ?? '';

  return (
    <g
      onClick={onSelect}
      style={{ cursor: 'pointer' }}
      aria-label={`${person.name} — ${
        fresh === 'live' ? 'live' : ageSentence(person.captured_at, serverTime)
      }`}
    >
      {fresh === 'live' ? (
        /* the live ring — a slow radar pulse; animation is decoration, the
         * WORD "live" under the marker is the claim */
        <circle cx={x} cy={y} r={r * 1.8} fill="none" stroke="var(--crf-status-success-text)" strokeWidth={r / 6}>
          <animate attributeName="r" values={`${String(r * 1.3)};${String(r * 2.2)}`} dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.8;0" dur="2s" repeatCount="indefinite" />
        </circle>
      ) : null}
      <circle
        cx={x}
        cy={y}
        r={r}
        fill={fresh === 'stale' ? 'var(--crf-bg-surface)' : hue}
        stroke={selected ? 'var(--crf-accent-indigo)' : 'var(--crf-text-secondary)'}
        strokeWidth={selected ? r / 4 : r / 8}
      />
      <text
        x={x}
        y={y + r * 0.36}
        textAnchor="middle"
        fontSize={r}
        fontWeight={600}
        fill={fresh === 'stale' ? 'var(--crf-text-secondary)' : 'var(--crf-brand-ink)'}
      >
        {initialsOf(person.name)}
      </text>
      {/* ⚠ THE AGE, ON THE CANVAS, ALWAYS — the marker never implies live */}
      <text
        x={x}
        y={y + r * 2.4}
        textAnchor="middle"
        fontSize={r * 0.75}
        fill={fresh === 'live' ? 'var(--crf-status-success-text)' : 'var(--crf-text-secondary)'}
      >
        {fresh === 'live' ? 'live' : ageSentence(person.captured_at, serverTime)}
      </text>
    </g>
  );
}
